import React from 'react';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import _merge from 'lodash/merge';
import { useTransition, animated } from 'react-spring';
import { TRANSITION_STANDARD } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import Styled from './styles';

export const SnackbarContext = React.createContext<any>({});

export interface SnackbarProps extends React.ComponentPropsWithRef<'div'> {
  /** Toggles whether the snackbar is open. */
  open?: boolean;
  /** onClose callback. Use this to control the opening/closing of the snackbar. */
  onClose?: () => void;
  /** Automatically close the snackbar after the 'autoCloseDuration' has passed. */
  autoClose?: boolean;
  /** Duration after which snackbar will begin to close. `autoClose` must be enabled. */
  autoCloseDuration?: number;
  /** Callback for when snackbar animation has ended and snackbar has fully closed. Useful when chaining multiple snackbars. See 'multiple' snackbars demo.  */
  onExited?(...args: unknown[]): unknown;
  /** The content of snackbar. See snackbar <SnackbarContent /> and <SnackbarAction /> */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Control animation config here. This uses react-spring transitions config. See https://react-spring.io for more information. When utilizing this prop to change 'initial, enter, leave' configs, be sure to include an 'isAnimating' property in the 'enter' config, with a value of 0. This flag is used to determine when animation has ended. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** @ignore */
  onMouseEnter?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  styles?: React.CSSProperties
}

/** Snackbar, place message here. All other props will be provided to the wrapper element. */
const Snackbar = React.forwardRef<HTMLElement, SnackbarProps>((props: SnackbarProps, ref) =>
  getContext(({ RTL, theme: globalTheme, disableAnimation }) => {
    const {
      open,
      animationProps,
      disableAnimation: propsdisableAnimation,
      children,
      theme,
      styles,
      onClose,
      autoClose,
      autoCloseDuration,
      onMouseEnter,
      onMouseLeave,
      onExited,
      ...otherProps
    } = props;

    const resolvedTheme = _merge({}, globalTheme.SNACKBAR, theme);
    const timerRef = React.useRef(null);
    const exitedRef = React.useRef(true);
    const mouseEnteredRef = React.useRef(false);
    // necessary to keep track of content height (and adjust styling accordingly).
    const [contentHeight, setContentHeight] = React.useState(0);

    React.useEffect(() => {
      // clear timer if open has been toggled.
      clearTimeout(timerRef.current);
    }, [open]);

    const setAutoCloseTimer = () => {
      if (autoClose) {
        timerRef.current = setTimeout(() => {
          if (open) {
            onClose && onClose();
          }
        }, autoCloseDuration);
      }
    };

    // animation logic
    const {
      onFrame, enter, leave, from, ...otherAnimationProps
    } = animationProps || {};
    // `isAnimating` is an arbitrary flag used to keep track of the animation stage
    const transitions = useTransition(open, null, {
      from: {
        isAnimating: 0, opacity: 0, transform: 'translate(-50%, 100%)', ...from
      },
      enter: {
        isAnimating: 1, opacity: 1, transform: 'translate(-50%, 0%)', ...enter
      },
      leave: {
        isAnimating: 0, opacity: 0, transform: 'translate(-50%, 100%)', ...leave
      },
      config: {
        duration: getDuration(SPEED_SLOW),
        easing: getEasing(TRANSITION_STANDARD)
      },
      onFrame: (active, type, values, ...args) => {
        // when enter animation finishes
        if ((type === 'enter' || type === 'update') && values.isAnimating === 1) {
          !mouseEnteredRef.current && setAutoCloseTimer();
          exitedRef.current = false;
        }
        // when leave animation finishes
        if (type === 'leave' && values.isAnimating === 0) {
          exitedRef.current = true;
          clearTimeout(timerRef.current);
          onExited && onExited();
        }
        onFrame && onFrame(active, type, values, ...args);
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
      ...otherAnimationProps
    });

    // used to stop the auto-close timer if the user is currently moused over the snackbar
    const handleOnMouseEnter = (e) => {
      clearTimeout(timerRef.current);
      mouseEnteredRef.current = true;
      onMouseEnter && onMouseEnter(e);
    };

    const handleOnMouseLeave = (e) => {
      setAutoCloseTimer();
      mouseEnteredRef.current = false;
      onMouseLeave && onMouseLeave(e);
    };

    // unmount if not open (and 'exited' animation fully concluded)
    if (!open && exitedRef.current) {
      return null;
    }

    return transitions.map(({ item, key, props: transitionsStyles }) =>
      item && (
      <Styled.Snackbar
        ref={ref}
        $rtl={RTL}
        $theme={resolvedTheme}
        $contentHeight={contentHeight}
        as={animated.div}
        key={key}
        style={{ ...transitionsStyles, ...styles }}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        aria-live="polite"
        {...otherProps}
      >
        <SnackbarContext.Provider value={{ setContentHeight }}>{children}</SnackbarContext.Provider>
      </Styled.Snackbar>
      ));
  }));

Snackbar.displayName = 'Snackbar';

Snackbar.defaultProps = {
  open: false,
  theme: {},
  animationProps: undefined,
  disableAnimation: undefined,
  onClose: undefined,
  autoClose: true,
  autoCloseDuration: 2000,
  onExited: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  styles: {}
};

export default Snackbar;
